import React, { useState } from 'react';
import { FiPlus, FiX, FiUpload, FiCheckCircle, FiTag, FiFolder, FiTrash2, FiMoreVertical } from 'react-icons/fi';

export default function FloatingActionButton({ 
  onUpload, 
  onSelect, 
  isSelectionMode,
  selectedCount,
  onCancelSelection,
  onTagSelected,
  onAddToAlbum,
  onDeleteSelected
}) {
  const [isOpen, setIsOpen] = useState(false);
  const [showSelectionMenu, setShowSelectionMenu] = useState(false);

  const toggleMenu = () => {
    setIsOpen(!isOpen);
  };

  const handleUpload = () => {
    setIsOpen(false);
    onUpload();
  };

  const handleSelect = () => {
    setIsOpen(false);
    onSelect();
  };

  const handleCancelSelection = () => {
    setShowSelectionMenu(false);
    onCancelSelection();
  };

  const handleTag = () => {
    setShowSelectionMenu(false);
    onTagSelected();
  };

  const handleAddToAlbum = () => {
    setShowSelectionMenu(false);
    onAddToAlbum();
  };

  const handleDelete = () => {
    setShowSelectionMenu(false);
    onDeleteSelected();
  };

  return (
    <>
      {/* Backdrop when menu is open */}
      {(isOpen || showSelectionMenu) && (
        <div 
          className="fab-backdrop" 
          onClick={() => {
            setIsOpen(false);
            setShowSelectionMenu(false);
          }}
        />
      )}

      {/* FAB Container */}
      <div className="fab-container">
        {/* Selection mode controls */}
        {isSelectionMode ? (
          <>
            {/* Selection count badge */}
            <div className="fab-selection-badge">
              {selectedCount} selected
            </div>

            {/* Selection action menu */}
            <div className={`fab-selection-actions ${showSelectionMenu ? 'open' : ''}`}>
              <button 
                className="fab-action"
                onClick={handleTag}
                disabled={selectedCount === 0}
              >
                <FiTag size={18} />
                <span>Tag</span>
              </button>
              <button 
                className="fab-action"
                onClick={handleAddToAlbum}
                disabled={selectedCount === 0}
              >
                <FiFolder size={18} />
                <span>Add to Album</span>
              </button>
              <button 
                className="fab-action fab-action-danger"
                onClick={handleDelete}
                disabled={selectedCount === 0}
              >
                <FiTrash2 size={18} />
                <span>Delete</span>
              </button>
            </div>

            {/* Selection control buttons */}
            <div className="fab-selection-controls">
              <button 
                className="fab-secondary"
                onClick={() => setShowSelectionMenu(!showSelectionMenu)}
                disabled={selectedCount === 0}
                aria-label="More actions"
              >
                <FiMoreVertical size={22} />
              </button>
              <button 
                className="fab-main fab-cancel"
                onClick={handleCancelSelection}
                aria-label="Cancel selection"
              >
                <FiX size={24} />
              </button>
            </div>
          </>
        ) : (
          <>
            {/* Normal mode action buttons */}
            <div className={`fab-actions ${isOpen ? 'open' : ''}`}>
              <button 
                className="fab-action"
                onClick={handleSelect}
                title="Select Images"
              >
                <FiCheckCircle size={20} />
                <span>Select</span>
              </button>
              <button 
                className="fab-action fab-action-primary"
                onClick={handleUpload}
                title="Upload Photos"
              >
                <FiUpload size={20} />
                <span>Upload</span>
              </button>
            </div>

            {/* Main FAB button */}
            <button 
              className={`fab-main ${isOpen ? 'open' : ''}`}
              onClick={toggleMenu}
              aria-label="Actions"
            >
              {isOpen ? <FiX size={24} /> : <FiPlus size={24} />}
            </button>
          </>
        )}
      </div>

      <style>{`
        .fab-backdrop {
          position: fixed;
          top: 0;
          left: 0;
          right: 0;
          bottom: 0;
          background: rgba(0, 0, 0, 0.3);
          z-index: 98;
          animation: fadeIn 0.2s ease-out;
        }

        @keyframes fadeIn {
          from {
            opacity: 0;
          }
          to {
            opacity: 1;
          }
        }

        .fab-container {
          position: fixed;
          bottom: calc(80px + env(safe-area-inset-bottom, 0px)); /* Above mobile nav */
          right: 16px;
          z-index: 99;
          display: flex;
          align-items: center;
          gap: 12px;
        }

        .fab-main {
          width: 56px;
          height: 56px;
          border-radius: 28px;
          background: var(--primary);
          color: white;
          border: none;
          box-shadow: 0 4px 12px rgba(0, 0, 0, 0.15);
          display: flex;
          align-items: center;
          justify-content: center;
          cursor: pointer;
          transition: all 0.3s cubic-bezier(0.4, 0, 0.2, 1);
        }

        .fab-main:active {
          transform: scale(0.95);
        }

        .fab-main.open {
          background: var(--text-secondary);
          transform: rotate(45deg);
        }

        .fab-main.fab-cancel {
          background: var(--text-secondary);
        }

        .fab-secondary {
          width: 48px;
          height: 48px;
          border-radius: 24px;
          background: var(--primary);
          color: white;
          border: none;
          box-shadow: 0 4px 12px rgba(0, 0, 0, 0.15);
          display: flex;
          align-items: center;
          justify-content: center;
          cursor: pointer;
          transition: all 0.3s cubic-bezier(0.4, 0, 0.2, 1);
        }

        .fab-secondary:disabled {
          opacity: 0.5;
          cursor: not-allowed;
        }

        .fab-secondary:not(:disabled):active {
          transform: scale(0.95);
        }

        .fab-selection-badge {
          background: var(--primary);
          color: white;
          padding: 8px 16px;
          border-radius: 20px;
          font-size: 14px;
          font-weight: 500;
          box-shadow: 0 2px 8px rgba(0, 0, 0, 0.15);
          white-space: nowrap;
        }

        .fab-selection-controls {
          display: flex;
          gap: 8px;
        }

        .fab-actions {
          display: flex;
          flex-direction: column;
          gap: 8px;
          position: absolute;
          bottom: 100%;
          right: 0;
          margin-bottom: 12px;
          opacity: 0;
          transform: translateY(20px);
          pointer-events: none;
          transition: all 0.3s cubic-bezier(0.4, 0, 0.2, 1);
        }

        .fab-actions.open {
          opacity: 1;
          transform: translateY(0);
          pointer-events: auto;
        }

        .fab-selection-actions {
          display: flex;
          flex-direction: column;
          gap: 8px;
          position: absolute;
          bottom: 100%;
          right: 0;
          margin-bottom: 12px;
          opacity: 0;
          transform: translateY(20px);
          pointer-events: none;
          transition: all 0.3s cubic-bezier(0.4, 0, 0.2, 1);
        }

        .fab-selection-actions.open {
          opacity: 1;
          transform: translateY(0);
          pointer-events: auto;
        }

        .fab-action {
          display: flex;
          align-items: center;
          gap: 12px;
          padding: 12px 20px;
          background: white;
          border: 1px solid var(--border-light);
          border-radius: 28px;
          box-shadow: 0 2px 8px rgba(0, 0, 0, 0.1);
          cursor: pointer;
          white-space: nowrap;
          font-size: 14px;
          font-weight: 500;
          color: var(--text);
          transition: all 0.2s ease;
        }

        .fab-action:disabled {
          opacity: 0.5;
          cursor: not-allowed;
        }

        .fab-action:not(:disabled):active {
          transform: scale(0.95);
        }

        .fab-action-primary {
          background: var(--primary);
          color: white;
          border-color: var(--primary);
        }

        .fab-action-danger {
          color: var(--danger);
        }

        .fab-action span {
          font-size: 14px;
        }

        /* Dark mode styles */
        .dark-mode .fab-action {
          background: var(--surface);
          border-color: var(--border);
        }

        .dark-mode .fab-action-primary {
          background: var(--primary);
          border-color: var(--primary);
        }

        .dark-mode .fab-selection-badge {
          background: var(--primary);
        }

        /* Hide on desktop */
        @media (min-width: 769px) {
          .fab-container,
          .fab-backdrop {
            display: none;
          }
        }
      `}</style>
    </>
  );
}
